import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef, useEffect, useState } from 'react';
import { SurveyCheckboxItem } from './survey-checkbox-item';
import { Input } from '~/components/ui/Input';

export interface Data {
  goals?: Goal[];
  goals_note?: string;
}

type Goal = 'build' | 'examples' | 'learn' | 'not-sure' | 'other';

const GOALS: Array<{ key: Goal; value: string }> = [
  { key: 'build', value: 'Build and launch an app / website' },
  { key: 'examples', value: 'Quickly build an example / prototype' },
  { key: 'learn', value: 'Learn how to build apps / websites' },
  { key: 'not-sure', value: 'Not sure yet' },
  { key: 'other', value: 'Other' },
];

interface Props {
  initialValues?: Data;
  className?: string;
  validate: () => void;
}

const SurveyStep5 = forwardRef<HTMLDivElement, Props>(({ initialValues = {}, className, validate }, ref) => {
  const [selectedGoals, setSelectedGoals] = useState<Goal[]>(
    Array.isArray(initialValues.goals) ? initialValues.goals : [],
  );

  const handleCheckboxChange = (value: Goal, checked: boolean) => {
    if (checked) {
      setSelectedGoals((prev) => [...prev, value]);
    } else {
      setSelectedGoals((prev) => prev.filter((goal) => goal !== value));
    }
  };

  useEffect(() => {
    validate();
  }, [selectedGoals]);

  return (
    <div className={className} ref={ref}>
      <h2 className="text-[32px]/[39px] font-semibold">What are you looking to achieve with Bolt? </h2>

      <h3 className="mt-8 text-bolt-ds-textSecondary italic">Please select all that apply</h3>

      <div className="mt-3 grid gap-3 w-full">
        {GOALS.map((goal) => (
          <SurveyCheckboxItem
            defaultChecked={selectedGoals.includes(goal.key)}
            key={goal.key}
            name="goals[]"
            value={goal.key}
            id={`goals-${goal.key}`}
            required={selectedGoals.length === 0}
            onCheckedChange={(checked) => handleCheckboxChange(goal.key, checked === 'indeterminate' ? true : checked)}
          >
            {goal.value}
          </SurveyCheckboxItem>
        ))}
      </div>

      <AnimatePresence onExitComplete={validate}>
        {selectedGoals.includes('other') && (
          <motion.div
            className="flex items-center w-full gap-2 mt-4"
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -5 }}
            onAnimationComplete={validate}
          >
            <label htmlFor="goals_note-input" className="text-bolt-ds-textSecondary text-sm font-medium">
              Please specify
            </label>

            <Input
              type="text"
              name="goals_note"
              id="goals_note"
              size="sm"
              placeholder="Describe your goal..."
              autoFocus
              required
              className="flex-1"
              defaultValue={initialValues?.goals_note}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

SurveyStep5.displayName = 'SurveyStep5';

export default SurveyStep5;
